import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { X, Upload, TreePine } from 'lucide-react';
import toast from 'react-hot-toast';
import { generateImageDescription } from '../lib/openai';
import { optimizeAndUploadImage } from '../utils/imageOptimizer';
import { createPortal } from 'react-dom';

interface PublicUploadTreeProps {
  onClose: () => void;
}

export default function PublicUploadTree({ onClose }: PublicUploadTreeProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    creator: '',
    email: '',
    acceptTerms: false
  });

  useEffect(() => {
    // Prevenir scroll del body cuando el modal está abierto
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleClose = () => {
    if (isUploading) {
      if (window.confirm('¿Estás seguro de que quieres cancelar la subida?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('El archivo es demasiado grande. Máximo 10MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Solo se permiten imágenes');
        return;
      }
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedFile || !formData.name || !formData.creator || !formData.email || !formData.acceptTerms) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setIsUploading(true);
    try {
      const downloadURL = await optimizeAndUploadImage(selectedFile, 'trees', {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        onProgress: (progress) => setUploadProgress(Math.round(progress))
      });
      
      const settingsDoc = await getDoc(doc(db, 'settings', 'ai-settings'));
      const prompt = settingsDoc.exists() ? settingsDoc.data().descriptionPrompt : '';
      
      const treeDoc = await addDoc(collection(db, 'trees'), {
        name: formData.name,
        creator: formData.creator,
        email: formData.email,
        imageUrl: downloadURL,
        createdAt: new Date(),
        status: 'pending',
        votes: 0,
        description: ''
      });

      if (prompt) {
        generateImageDescription(downloadURL, prompt).then(async (desc) => {
          if (desc) {
            await updateDoc(doc(db, 'trees', treeDoc.id), { description: desc });
          }
        }).catch(console.error);
      }

      toast.success('¡Tu árbol ha sido enviado con éxito!');
      setShowSuccess(true);
    } catch (error) {
      console.error('Error general:', error);
      toast.error('Error al procesar la subida');
    } finally {
      setIsUploading(false);
    }
  };

  const handleModalClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[1000] overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4"
      onClick={handleModalClick}
    >
      <div className="bg-white rounded-xl max-w-xl w-full relative" onClick={e => e.stopPropagation()}>
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 z-10"
          aria-label="Cerrar"
        >
          <X className="h-6 w-6" />
        </button>

        {showSuccess ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TreePine className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              ¡Árbol enviado con éxito!
            </h3>
            <p className="text-gray-600 mb-6">
              Tu árbol será revisado por nuestro equipo. Te notificaremos por email cuando sea aprobado.
            </p>
            <button
              onClick={handleClose}
              className="bg-primary hover:bg-primary-dark text-white px-6 py-2 rounded-lg transition-colors"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Inscribe tu Árbol de Navidad
            </h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de tu árbol
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Ej: Mi Árbol Mágico"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tu nombre
                </label>
                <input
                  type="text"
                  name="creator"
                  value={formData.creator}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Nombre completo"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="tu@email.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Foto de tu árbol
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="tree-photo"
                    required
                  />
                  <label
                    htmlFor="tree-photo"
                    className="cursor-pointer flex flex-col items-center justify-center"
                  >
                    {preview ? (
                      <img
                        src={preview}
                        alt="Preview"
                        className="max-h-64 object-contain mb-4"
                      />
                    ) : (
                      <div className="text-center">
                        <Upload className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600">
                          Haz clic para seleccionar una foto
                        </p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              <div className="flex items-start">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleInputChange}
                  className="mt-1 mr-2"
                  required
                />
                <label className="text-sm text-gray-600">
                  Acepto las bases del concurso y la política de privacidad
                </label>
              </div>

              <button
                type="submit"
                disabled={isUploading}
                className="w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center"
              >
                {isUploading ? (
                  <>
                    <div className="mr-2">Subiendo... {uploadProgress}%</div>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </>
                ) : (
                  'Enviar participación'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  );
}